/**
 * <gift-wrap-options>
 * Product-page gift wrap selector (paid, multi-tier, per-product).
 *
 * Each radio maps to a variant of a hidden "gift wrap" product. When the customer
 * adds the product to cart, the selected gift-wrap variant is added as a separate
 * line item in the SAME /cart/add request, so both items land together and the
 * cart drawer opens once. If "No gift wrap" is selected, the theme's normal
 * add-to-cart flow runs untouched.
 *
 * Notes:
 * - The submit is intercepted at the document level (capture phase) and the form
 *   is resolved at submit time, so placement above/below the buy-buttons is fine.
 * - The form is matched via getAttribute('id'), NOT form.id: the product form has
 *   an <input name="id"> (variant id) which overrides the form's .id property.
 * - /cart/add with an items[] array is atomic; if the gift line can't be added
 *   (unpublished / out of stock) we log the reason and fall back to adding the
 *   product alone so the sale is never blocked.
 */
if (!customElements.get('gift-wrap-options')) {
  class GiftWrapOptions extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this._onSubmit = this.handleSubmit.bind(this);
      document.addEventListener('submit', this._onSubmit, true);

      // Hide the express/Buy-it-now button while a paid gift is selected, since an
      // accelerated checkout can't carry the separate gift-wrap product.
      this.addEventListener('change', (event) => {
        if (event.target.matches('input[data-gift-wrap-option]')) this.updateExpressCheckout();
      });
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.updateExpressCheckout(), { once: true });
      } else {
        this.updateExpressCheckout();
      }
      // Shop Pay hydrates late — reapply after full load.
      window.addEventListener('load', () => this.updateExpressCheckout(), { once: true });
    }

    disconnectedCallback() {
      if (this._onSubmit) document.removeEventListener('submit', this._onSubmit, true);
    }

    get productForm() {
      // getElementById is safe here (not affected by the form's named-control override).
      return document.getElementById(`product-form-${this.sectionId}`);
    }

    updateExpressCheckout() {
      const form = this.productForm;
      if (!form) return;
      const hide = !!this.selectedVariantId; // a paid gift is selected
      const buttons = form.querySelectorAll(
        '.shopify-payment-button, shopify-accelerated-checkout, shopify-accelerated-checkout-cart'
      );
      buttons.forEach((el) => {
        el.style.display = hide ? 'none' : '';
        el.toggleAttribute('inert', hide);
      });
    }

    get selectedInput() {
      return this.querySelector('input[data-gift-wrap-option]:checked');
    }
    get selectedVariantId() {
      const input = this.selectedInput;
      return input && input.value ? input.value : null;
    }
    get giftNote() {
      const note = this.querySelector('[data-gift-note]');
      return note && note.value.trim() ? note.value.trim() : null;
    }

    isOurForm(el) {
      return (
        el &&
        el.tagName === 'FORM' &&
        el.getAttribute('is') === 'product-form' &&
        el.getAttribute('id') === `product-form-${this.sectionId}`
      );
    }

    handleSubmit(event) {
      if (!this.isOurForm(event.target)) return;

      const variantId = this.selectedVariantId;
      if (!variantId) return; // No gift chosen -> let the theme add the product normally.

      event.preventDefault();
      event.stopImmediatePropagation();
      this.form = event.target;
      this.addWithGift(variantId);
    }

    setLoading(state) {
      const btn = this.form && this.form.querySelector('[type="submit"]');
      if (!btn) return;
      btn.classList.toggle('btn--loading', !!state);
      state ? btn.setAttribute('aria-disabled', 'true') : btn.removeAttribute('aria-disabled');
    }

    get productTitle() {
      return this.dataset.productTitle || '';
    }

    buildItems(variantId) {
      const formData = new FormData(this.form);
      const quantity = parseInt(formData.get('quantity') || '1', 10) || 1;

      const input = this.selectedInput;
      const label = (input && input.dataset.giftLabel) || 'Yes';

      // Product line: stamp the chosen wrap so each line shows its own box.
      // This keeps things unambiguous when several products are ordered together,
      // and forces separate lines when different products pick different wraps.
      const properties = { 'Gift wrap': label };
      formData.forEach((value, key) => {
        const m = key.match(/^properties\[(.+)\]$/);
        if (m) properties[m[1]] = value;
      });
      const mainItem = { id: formData.get('id'), quantity: quantity, properties: properties };

      // Gift line: one box per item (quantity matches the product), linked back
      // to the product it belongs to for fulfilment.
      const giftProperties = { _gift_wrap: label };
      if (this.productTitle) giftProperties._for = this.productTitle;
      const note = this.giftNote;
      if (note) giftProperties['Gift note'] = note;

      return [
        mainItem,
        { id: parseInt(variantId, 10), quantity: 1, properties: giftProperties },
      ];
    }

    requestAdd(items) {
      const body = JSON.stringify({
        items: items,
        sections: this._sections || '',
        sections_url: window.location.pathname,
      });
      const config = FoxTheme.utils.fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.body = body;
      return fetch(`${FoxTheme.routes.cart_add_url}`, config).then((response) => response.json());
    }

    addWithGift(variantId) {
      this.setLoading(true);

      const items = this.buildItems(variantId);

      let sections = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
      );
      this._sections = sections.join(',');

      this.requestAdd(items)
        .then((parsedState) => {
          if (parsedState && parsedState.status) {
            console.error(
              '[gift-wrap-options] Gift wrap could not be added. Check that the gift-wrap ' +
                'product is published to the Online Store sales channel and purchasable. ' +
                'Adding the product without gift wrap. Server response:',
              parsedState
            );
            return this.requestAdd([items[0]]);
          }
          return parsedState;
        })
        .then((parsedState) => {
          if (!parsedState || parsedState.status) {
            this.displayError((parsedState && (parsedState.description || parsedState.message)) || null);
            return;
          }
          return this.finishAdd(parsedState);
        })
        .catch((error) => console.error('Gift wrap add error:', error))
        .finally(() => this.setLoading(false));
    }

    async finishAdd(parsedState) {
      const cartJson = await (
        await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig('json', 'GET') })
      ).json();
      cartJson['sections'] = parsedState['sections'];

      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
      document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: parsedState } }));

      if (document.body.classList.contains('cart-template') || FoxTheme.settings.cartType === 'page') {
        window.location = FoxTheme.routes.cart_url;
        return;
      }
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.show === 'function') drawer.show();
    }

    displayError(message) {
      const wrapper = this.form && this.form.querySelector('.product-form__error-message');
      if (wrapper && message) {
        wrapper.toggleAttribute('hidden', false);
        wrapper.textContent = message;
      }
    }
  }

  customElements.define('gift-wrap-options', GiftWrapOptions);
}
